import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
//#region types
import { WintSuperkollAPIModelTimeReporting as ApiModel, WintUtilsWebApi } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
import { BusinessUnitFormTypes } from "models/formTypes";
import { TableRowCommonKeys, TableRowProperties } from "models/propTypes/simpleTableProps";
import { HourEnums } from "models/enums";
import { Hours } from "models/utilTypes/hourType";
import { RouteConstants } from "utils/constants";
//#endregion
//#region components
import { TableAsync, useFilterContext, useSearchContext } from "components/common";
import { useBusinessUnitService } from "serviceHooks";
//#endregion
import { DateHelper, UrlHelper } from "utils/helpers";

const BusinessUnitsList: React.FC = () => {
  const businessUnitService = useBusinessUnitService();
  const location = useLocation();
  const navigate = useNavigate();
  const { filter } = useFilterContext<BusinessUnitFormTypes.Filter>();
  const searchContext = useSearchContext();

  const filterFunc = React.useCallback(
    (pagination: WintUtilsWebApi.Filter) => {
      return businessUnitService.businessUnitsReport(pagination, {
        ...(filter ?? {}),
        businessUnitManagerId: filter?.businessUnitManagerId as number,
        businessUnitName: searchContext?.searchText,
        businessUnitId: filter?.businessUnitId ?? "",
      });
    },
    [filter, searchContext?.searchText]
  );

  const onRowClick = (row: ApiModel.BusinessUnitReport) => {
    const yearMonth = DateHelper.getYearMonth(filter?.period ?? new Date()).toString();
    const periodQuery = `${yearMonth.substring(0, 4)}-${yearMonth.substring(4, 6)}`;
    navigate(
      UrlHelper.formatPath(`/BusinessUnits/${row.id}?${RouteConstants.TimeReportingQueryParams.Period}=${periodQuery}`),
      {
        state: { background: location, preserveSearch: true },
      }
    );
  };

  const tableProperties: Partial<
    Record<keyof TableRowCommonKeys | keyof ApiModel.BusinessUnitReport, TableRowProperties>
  > = {
    businessUnitName: {
      description: TranslationKeys.BusinessUnit.BusinessUnitName,
      helpText: TranslationKeys.BusinessUnit.HelpTextBusinessUnitName,
    },
    businessUnitManager: {
      description: TranslationKeys.BusinessUnit.Manager,
      helpText: TranslationKeys.BusinessUnit.HelpTextManager,
    },
    numberOfMembers: {
      description: TranslationKeys.BusinessUnit.NumberOfMembers,
      helpText: TranslationKeys.BusinessUnit.HelpTextNumberOfMembers,
      alignment: "right",
      classification: "number",
    },
    internalTime: {
      description: TranslationKeys.TimeReporting.InternalTime,
      helpText: TranslationKeys.BusinessUnit.HelpTextInternalTime,
      formatter: (r: ApiModel.BusinessUnitReport) => new Hours(r.internalTime, HourEnums.HourCode.SHORT),
      alignment: "right",
    },
  };

  return (
    <TableAsync<ApiModel.BusinessUnitReport>
      properties={tableProperties}
      filter={filterFunc}
      rowClickHandler={onRowClick}
      pagination={"advanced"}
      maxRowCount={10}
      orderByProperty="businessUnitName"
      orderByDescending={false}
    />
  );
};
export default BusinessUnitsList;
